// TODO: Break this file apart and modularize it a bit.
import React, { useEffect, useMemo, type PropsWithChildren } from "react";

import { useAtom } from "jotai";
import { atomWithStorage } from "jotai/utils";
import { Navigate, useLocation } from "react-router-dom";

import useAllowedNonprefixedPage from "@hooks/use-allowed-nonprefixed-page";
import { identify, init, logEvent } from "@internal/analytics";
import { userManager, type User } from "@internal/user";

import { useAuth0 } from "./auth0-wrapper";
import NoUserScreen from "./error-pages/no-user-screen";
import { LoadingScreen } from "./title-slide-view";

interface IUserContext {
  user: User;
  actingUser: {
    actingUserId: ReturnType<typeof useActingUserId>[0];
    setActingUserId: ReturnType<typeof useActingUserId>[1];
  };
}

interface UserOptions {
  urlUserId: string;
  children: React.ReactElement | React.ReactElement[];
}

export const UserContext = React.createContext<IUserContext | null>(null);
const actingUserIdAtom = atomWithStorage<null | string>("actingUserId", null);

export const useActingUserId = () => useAtom(actingUserIdAtom);

export const useUser = () => React.useContext(UserContext)!;

export const UserProvider = ({ urlUserId, children }: UserOptions) => {
  const { pathname } = useLocation();

  const {
    loading,
    user: auth0User,
    isAuthenticated,
    loginWithRedirect,
    getTokenSilently,
  } = useAuth0();

  const [userLoading, setUserLoading] = React.useState(true);
  const [user, setUser] = React.useState<User | null>();

  const [actingUserId, setActingUserId] = useActingUserId();
  const inOnboarding =
    user?.deactivatedAt || !user?.featureFlags["customerOnboardingFinished"];

  const impersonatorPath = useMemo(() => {
    // if there already is a url user id, we don't need to do anything
    if (urlUserId !== "") {
      return "";
    }

    const pathParams = pathname.split("/");
    // if there is a page route, then use it
    if (pathParams.length >= 2) {
      return pathParams.slice(1).join("/");
    }
    return "overview";
  }, [pathname, urlUserId]);

  React.useEffect(() => {
    if (!loading) {
      if (!isAuthenticated) {
        /* If not logged in, log in, and figure it out from there.
           This action refreshes the page. */
        (async () => {
          await loginWithRedirect();
        })();
      } else {
        // Wait for user to (eventually) become available.
        if (!auth0User) {
          return;
        }

        // Make a call to the internal API to fetch user state.
        (async () => {
          try {
            if (!user || user.displayUserId !== urlUserId) {
              const email = auth0User.name;
              /* setUserLoading here prevents anything from being rendered while the
                 user object is being switched. */
              setUserLoading(true);

              const userToSet = await userManager.create(
                email,
                {
                  getTokenSilently,
                  impersonateUserId: urlUserId,
                },
                actingUserId ?? undefined,
              );

              if (userToSet) {
                if (window.Appcues) {
                  window.Appcues.identify(
                    userToSet.authedUserId, // unique, required
                    {
                      createdAt: userToSet.authedUser.createdAt, // Unix timestamp of user signup date

                      role: "User", // Current user's role or permissions
                      isNorthbeamAdmin: userToSet.isAdmin,
                      clientId: userToSet.displayUserId,

                      companyName: userToSet.companyName, // Current user's company name
                      email: userToSet.authedUser?.email, // Current user's email

                      version: "2.0", // users on different versions may need to see different content
                    },
                  );

                  window.Appcues.group(userToSet.displayUserId, {});
                }

                /* This only happens when:
                   1. The page is refreshed
                   2. The user is switched from the dropdown
                   3. A user logs in */
                init(userToSet);
                identify(userToSet);

                logEvent("Initialize new user", {
                  isDashboardV2Enabled:
                    userToSet.featureFlags.enableDashboardV2 ?? false,
                });
              }
              setUser(userToSet);
            }
          } finally {
            setUserLoading(false);
          }
        })();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    loading,
    isAuthenticated,
    loginWithRedirect,
    auth0User,
    getTokenSilently,
    urlUserId,
  ]);

  const isAllowedNonPrefixedPage = useAllowedNonprefixedPage();

  if (userLoading) {
    return <LoadingScreen />;
  }

  if (!user) {
    return <NoUserScreen />;
  }

  if (!urlUserId && !isAllowedNonPrefixedPage) {
    // Directing straight to overview/onboarding saves us a page load
    if (!inOnboarding) {
      return <Navigate to={`${user.displayUserId}/${impersonatorPath}`} />;
    } else {
      return <Navigate to={`${user.displayUserId}/onboarding`} />;
    }
  }

  return (
    <UserContext.Provider
      value={{
        user: user,
        actingUser: {
          actingUserId,
          setActingUserId,
        },
      }}
    >
      <AppCuesPageWrapper>{children}</AppCuesPageWrapper>
    </UserContext.Provider>
  );
};

function AppCuesPageWrapper({ children }: PropsWithChildren<unknown>) {
  const location = useLocation();

  useEffect(() => {
    if (window.Appcues) {
      window.Appcues.page();
    }
  }, [location.pathname]);

  return children;
}
